import { useCallback, useEffect, useState } from "react";
import { usePostHog } from "@posthog/react";
import { User } from "@/lib/api/user";
import { setPostHogInstance } from "@/lib/posthog-events";

// Type declaration for PostHog global
declare global {
  interface Window {
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
      identify: (id: string, properties?: Record<string, unknown>) => void;
      reset: () => void;
    };
  }
}

// Check if we should track analytics (not in development)
const isProduction = import.meta.env.VITE_STYTCH_PUBLIC_ENV !== "development";

export const getPostHogPersonProperties = (user: User) => ({
  name: user.name,
  role: user.role,
  email: user.email,
  groups: user.assigned_groups?.map((group) => group.name) ?? [],
});

export const usePostHogSetup = (user: User | null) => {
  const posthog = usePostHog();
  const [isLogging, setIsLogging] = useState(false);

  // Set up user identification and properties (runs in all envs for feature flags)
  useEffect(() => {
    if (posthog) setPostHogInstance(posthog);
    if (user && posthog) {
      posthog.identify(user.id, getPostHogPersonProperties(user));
      posthog.reloadFeatureFlags();
    }

    // Cleanup function
    return () => {
      if (posthog) {
        posthog.reset();
      }
    };
  }, [user, posthog]);

  // Track session events
  useEffect(() => {
    if (user && isProduction && posthog) {
      if (!isLogging) {
        // Track session start
        posthog.capture("session_started");
        setIsLogging(true);
      }

      // Cleanup function - track session end
      return () => {
        if (isLogging) {
          posthog.capture("session_ended");
          setIsLogging(false);
        }
      };
    }
  }, [user, isLogging, posthog]);
};

export const useUpdatePostHogUser = () => {
  const posthog = usePostHog();

  const updatePostHogUser = useCallback(
    (user: User) => {
      if (!posthog || !user.id) return;

      posthog.identify(user.id, getPostHogPersonProperties(user));
      posthog.reloadFeatureFlags();
    },
    [posthog],
  );

  return { updatePostHogUser };
};

export const usePostHogEvent = (
  event: string,
  options?: { onMount?: boolean; params?: Record<string, unknown> },
) => {
  const posthog = usePostHog();
  const { onMount, params } = options || {};

  const dispatchEvent = useCallback(
    (updatedEvent?: string, updatedParams?: Record<string, unknown>) => {
      if (!isProduction || !posthog) return;
      posthog.capture(updatedEvent || event, { ...params, ...updatedParams });
    },
    [event, params, posthog],
  );

  useEffect(() => {
    if (onMount) {
      dispatchEvent();
    }
  }, [onMount, dispatchEvent]);

  return { dispatchEvent };
};

// Helper for direct tracking without hooks
export const PostHogTracking = {
  track: (eventName: string, properties?: Record<string, unknown>) => {
    if (isProduction && window.posthog) {
      window.posthog.capture(eventName, properties);
    }
  },

  identify: (id: string, properties?: Record<string, unknown>) => {
    if (window.posthog) {
      window.posthog.identify(id, properties);
    }
  },

  reset: () => {
    if (isProduction && window.posthog) {
      window.posthog.reset();
    }
  },
};

// Legacy exports for backward compatibility - these will use PostHog under the hood
